/**
 * Parent-side hash-anchor scroll fix.
 *
 * When the host page is opened with a #hash whose target sits BELOW an
 * auto-resizing iframe, the browser scrolls before the iframe reports its
 * height. The iframe then grows, pushing the target away, so the anchor
 * lands in the wrong place.
 *
 * This controller re-scrolls the host page to the hash target after each
 * layout change, until the layout goes quiet — and never once the user has
 * scrolled themselves (hard, permanent abort).
 *
 * Robustness notes (host CSS / behaviour we can't control):
 * - Hosts animate iframe height (`transition: height …`) and lazy-load content,
 *   so growth lands at unpredictable times. We watch the host layout with a
 *   ResizeObserver and keep re-pinning until it stays quiet for IDLE_STOP_MS
 *   (a fixed time window would be flaky against variable load times).
 * - Hosts set `html { scroll-behavior: smooth }`. The browser's own initial
 *   scroll-to-hash then ANIMATES, emitting scroll events that look like the
 *   user and would abort us. We force `scroll-behavior: auto` for the whole
 *   active window so every scroll (browser's and ours) is instant, and we only
 *   arm the scroll-based user detection AFTER our first pin (the browser's
 *   initial jump happens before that and must be ignored).
 */
import { debugLog } from './utils';
const DEBOUNCE_MS = 150;
/** A scroll event further than this from our last programmatic scroll counts as the user. */
const SCROLL_DELTA_TOLERANCE = 4;
/** Don't re-scroll if the target moved less than this since last correction. */
const TARGET_DRIFT_TOLERANCE = 2;
/**
 * Stop this long after the LAST host layout change. Re-pinning follows the
 * actual layout, not a fixed clock — widgets finish loading (and grow) at
 * unpredictable times. `hashScrollWindowMs` is only an absolute safety cap.
 */
const IDLE_STOP_MS = 1500;
const SCROLL_KEYS = new Set([
    'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar',
]);
export function createHashScrollController(config, logPrefix) {
    const { enableHashScrollFix, hashScrollOffset, hashScrollWindowMs, debugMode } = config;
    const armed = enableHashScrollFix &&
        typeof window !== 'undefined' &&
        typeof location !== 'undefined' &&
        location.hash.length > 1;
    // No-op controller when there's nothing to fix.
    if (!armed) {
        return { scheduleRestore() { }, destroy() { } };
    }
    const targetId = decodeURIComponent(location.hash.slice(1));
    const root = document.documentElement;
    let aborted = false;
    let pinnedOnce = false;
    let lastTargetY = null;
    let debounceTimer = null;
    let idleTimer = null;
    let capTimer = null;
    let resizeObserver = null;
    let isProgrammaticScrolling = false;
    let lastProgrammaticY = 0;
    // Force instant scrolling for the whole active window so the host's
    // `scroll-behavior: smooth` doesn't animate (and spuriously look like a user
    // scroll). Restored on stop().
    const prevScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    const stop = () => {
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }
        if (idleTimer) {
            clearTimeout(idleTimer);
            idleTimer = null;
        }
        if (capTimer) {
            clearTimeout(capTimer);
            capTimer = null;
        }
        if (resizeObserver) {
            resizeObserver.disconnect();
            resizeObserver = null;
        }
        root.style.scrollBehavior = prevScrollBehavior;
        window.removeEventListener('wheel', abort);
        window.removeEventListener('touchmove', abort);
        window.removeEventListener('keydown', onKey);
        window.removeEventListener('scroll', onScroll);
    };
    const abort = () => {
        if (aborted)
            return;
        aborted = true;
        debugLog(debugMode, logPrefix, 'Hash-scroll aborted (user scrolled)');
        stop();
    };
    const onKey = (e) => {
        if (SCROLL_KEYS.has(e.key))
            abort();
    };
    // Catches scrollbar drag, which fires no wheel/touch event. Only armed after
    // our first pin — the browser's initial scroll-to-hash happens before that
    // and must not be mistaken for the user.
    const onScroll = () => {
        if (aborted || isProgrammaticScrolling || !pinnedOnce)
            return;
        if (Math.abs(window.scrollY - lastProgrammaticY) > SCROLL_DELTA_TOLERANCE)
            abort();
    };
    const attempt = () => {
        if (aborted)
            return;
        const el = document.getElementById(targetId);
        if (!el)
            return;
        const rect = el.getBoundingClientRect();
        const targetY = Math.max(0, Math.round(rect.top + window.pageYOffset - hashScrollOffset));
        // Skip redundant scrolls once the target stops drifting.
        if (lastTargetY !== null && Math.abs(targetY - lastTargetY) <= TARGET_DRIFT_TOLERANCE)
            return;
        lastTargetY = targetY;
        isProgrammaticScrolling = true;
        lastProgrammaticY = targetY;
        window.scrollTo(0, targetY);
        // Record the position actually reached (may differ near page bottom), then
        // release the flag so genuine user scrolls are detected again.
        requestAnimationFrame(() => {
            lastProgrammaticY = window.scrollY;
            pinnedOnce = true;
            requestAnimationFrame(() => { isProgrammaticScrolling = false; });
        });
        debugLog(debugMode, logPrefix, 'Hash-scroll re-pin to', targetY);
    };
    const scheduleRestore = () => {
        if (aborted)
            return;
        if (debounceTimer)
            clearTimeout(debounceTimer);
        debounceTimer = setTimeout(attempt, DEBOUNCE_MS);
        // Keep alive while the layout is still changing; stop once it goes quiet.
        if (idleTimer)
            clearTimeout(idleTimer);
        idleTimer = setTimeout(stop, IDLE_STOP_MS);
    };
    window.addEventListener('wheel', abort, { passive: true });
    window.addEventListener('touchmove', abort, { passive: true });
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    // Re-pin on ANY host layout change (covers height-transition animations and
    // lazy content that grow without emitting a height message).
    if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
            if (isProgrammaticScrolling)
                return; // our scroll doesn't change layout, but be safe
            scheduleRestore();
        });
        resizeObserver.observe(root);
    }
    // Absolute safety cap — stop no matter what after this long.
    capTimer = setTimeout(stop, hashScrollWindowMs);
    debugLog(debugMode, logPrefix, 'Hash-scroll fix armed for #' + targetId);
    // Initial attempt in case layout is already settled.
    scheduleRestore();
    return {
        scheduleRestore,
        destroy() {
            aborted = true;
            stop();
        },
    };
}
